<template>
    <common-page-loader :isLoading="isLoading">
        <div :class="appLayoutClass">
            <div :class="overlayClass"></div>
            <header
                v-click-away="closeSidenav"
                :class="sidenavClass"
                @mouseenter="sideNavMouseOver()"
                @mouseleave="sideNavMouseLeave()"
            >
                <app-sidenav
                    :isSmall="!isLargeSideNav"
                    :isSidenavOpen="isSidenavOpen"
                    @close-sidenav="closeSidenav"
                ></app-sidenav>
            </header>

            <nav class="main-layout__topnav">
                <app-topnav @open-sidenav="openSidenav"></app-topnav>
            </nav>

            <main :class="contentClass">
                <slot></slot>
                <div id="modal-target"></div>
            </main>
        </div>
        <complete-profile-modal
            v-if="isCompleteProfileModalOpen"
            @close="isCompleteProfileModalOpen = false"
        ></complete-profile-modal>
        <helpdesk v-if="isHomePage"></helpdesk>
        <snackbar
            v-if="isSnackbarShown"
            :title="snackbarTitle"
            :buttonLabel="snackbarButtonLabel"
            :showSnackContent="!showSnackContent"
            @close="hideSnackbar"
            @snackbar-action="snackbarAction"
        >
            <template v-if="showSnackContent" #expand>
                <div v-for="(item, index) in outstandingsAmountCompanies" :key="index" class="snackbar__slot">
                    <div class="snackbar__content--title">
                        {{
                            $t('pages.company.my_company.outstandingsAmountCompany', {
                                n: roundOutstandingAmount(item.outstandingAmount.outstandingAmount),
                                m: item.outstandingAmount.currency,
                                c: item.company.label,
                            })
                        }}
                    </div>
                    <div class="snackbar__content--action">
                        <common-button
                            type="primary"
                            :label="$t('pages.company.my_company.tabs.invoices')"
                            size="micro"
                            @click="goToCompanyInvoice(item.company.route)"
                        ></common-button>
                    </div>
                </div>
            </template>
        </snackbar>
        <cookie-modal
            v-if="showCookiesModal && !userIsOnIphone"
            @cookiesAccepted="cookiesAccepted"
            @close="declineCookies"
        ></cookie-modal>
    </common-page-loader>
</template>

<script>
    import Store from '@/store'
    import Helpdesk from 'components/page-elements/helpdesk/Helpdesk'
    import Snackbar from 'components/page-elements/snackbar/Snackbar'
    import { importComponents } from '~/helpers'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import VersionUpdateServiceWorkerMixin from 'mixins/VersionUpdateServiceWorkerMixin'
    import RoutesEnum from 'enums/routes.enum'
    import CompleteProfileModal from 'components/modals/CompleteProfileModal'
    import CookieModal from 'components/modals/CookieModal'
    import { loadFacebookPixel } from '../../meta-pixel'
    import { googleOneTap } from 'vue3-google-login'
    import LoginType from 'enums/login_type.enum'
    import AuthAPI from 'api/AuthAPI'
    import AuthService from 'services/AuthService'
    import CompanyAPI from 'api/CompanyAPI'
    import CompanyRoles from 'enums/company_roles.enum'

    export default {
        mixins: [WindowResizeMixin, VersionUpdateServiceWorkerMixin],

        components: {
            ...importComponents(require.context('components/layouts/components/main-layout')),
            Helpdesk,
            CompleteProfileModal,
            CookieModal,
            Snackbar,
        },

        props: {
            contentPadding: { type: String, default: 'small' },
        },

        data() {
            return {
                isSidenavOpen: false,
                isSideNavHovered: false,
                isCompleteProfileModalOpen: false,
                showCookiesModal: !localStorage.getItem('cookieConsent'),
                outstandingsAmountCompanies: [],
                showSnackContent: false,
                isSnackbarShown: false,
                userIsOnIphone: localStorage.getItem('deviceIphone'),
            }
        },

        computed: {
            isHomePage() {
                return this.$route.name === RoutesEnum.INDEX.HOME
            },
            isLargeSideNav() {
                return this.isSideNavHovered && !this.isTablet
            },

            appLayoutClass() {
                return {
                    'main-layout': true,
                    'main-layout__hover': this.isLargeSideNav,
                }
            },

            sidenavClass() {
                return {
                    'main-layout__sidenav': true,
                    'main-layout__sidenav--closed': !this.isSidenavOpen,
                    'main-layout__sidenav--large': this.isLargeSideNav,
                }
            },

            overlayClass() {
                return {
                    'main-layout__overlay': this.isSidenavOpen,
                }
            },

            contentClass() {
                return {
                    'main-layout__content': true,
                    [`main-layout__content--padding-size-${this.contentPadding}`]: true,
                }
            },

            isLoading() {
                return Store.getters['configuration/getIsLoading']
            },

            userCompanies() {
                return (
                    this.$auth
                        .getUser()
                        .companyUsers.filter((item) => item.companyRole === CompanyRoles.COMPANY_ADMIN.VALUE)
                        .map((cr) => ({
                            route: {
                                name: RoutesEnum.COMPANY.MY_COMPANY,
                                params: { id: cr.company?.id.toString(), tabType: 'invoices' },
                                matchFullRoute: true,
                            },
                            label: cr.company?.name,
                            id: cr.company?.id,
                        })) || []
                )
            },
            snackbarTitle() {
                return this.outstandingsAmountCompanies.length > 1
                    ? this.$t('pages.company.my_company.outstandingsAmountCompanies')
                    : this.$t('pages.company.my_company.outstandingsAmountCompany', {
                          n: this.roundOutstandingAmount(this.outstandingsAmountCompanies[0].outstandingAmount.outstandingAmount),
                          m: this.outstandingsAmountCompanies[0].outstandingAmount.currency,
                          c: this.outstandingsAmountCompanies[0].company.label,
                      })
            },
            snackbarButtonLabel() {
                return this.outstandingsAmountCompanies.length > 1
                    ? this.$t('common.microcopy.view')
                    : this.$t('pages.company.my_company.tabs.invoices')
            },
        },

        methods: {
            closeSidenav() {
                this.isSidenavOpen = false
            },

            openSidenav() {
                this.isSidenavOpen = true
            },

            sideNavMouseLeave() {
                if (this.isLoading) {
                    return
                }
                this.isSideNavHovered = false
            },

            sideNavMouseOver() {
                this.isSideNavHovered = true
            },

            showCompleteProfileModal() {
                if (
                    this.$auth.getUser().id &&
                    this.$auth.isLoggedIn() &&
                    !this.$auth.isUserCompleteProfile() &&
                    !localStorage.getItem('completeProfileModalShown')
                ) {
                    localStorage.setItem('completeProfileModalShown', true)
                    this.isCompleteProfileModalOpen = true
                }
            },
            cookiesAccepted() {
                localStorage.setItem('cookieConsent', true)
                this.showCookiesModal = false
                loadFacebookPixel()
            },
            declineCookies() {
                localStorage.setItem('cookieConsent', false)
                this.showCookiesModal = false
            },

            createSession(data) {
                this.$auth.createSession({
                    accessToken: data.access_token,
                    refreshToken: data.refresh_token,
                    loginType: LoginType.GOOGLE,
                })
                window.location.reload()
            },

            showGSignupButton() {
                return !navigator.userAgent.includes('Instagram') && !navigator.userAgent.match(/FBAN|FBAV/i)
            },

            companyOutstandingAmount() {
                Promise.all(
                    this.userCompanies.map((company) =>
                        CompanyAPI.getCompanyOutstandingAmount(company.id).then((val) => {
                            if (val.outstandingAmount !== null) {
                                return {
                                    company: company,
                                    outstandingAmount: val,
                                }
                            }
                            return null
                        }),
                    ),
                ).then((results) => {
                    this.outstandingsAmountCompanies = results.filter(
                        (item) => item !== null && item.outstandingAmount?.outstandingAmount > 0,
                    )
                    this.showSnackbar()
                })
            },

            goToCompanyInvoice(route) {
                this.hideSnackbar()
                this.$router.push(route)
            },

            roundOutstandingAmount(amount) {
                return Math.round(amount)
            },

            snackbarAction() {
                if (this.outstandingsAmountCompanies.length > 1) {
                    this.showSnackContent = true
                } else {
                    this.goToCompanyInvoice(this.outstandingsAmountCompanies[0].company.route)
                }
            },

            showSnackbar() {
                const hideUntil = localStorage.getItem('hideSnackbarUntil')
                const currentTime = new Date().getTime()

                if (
                    (!hideUntil || currentTime > hideUntil) &&
                    this.outstandingsAmountCompanies.length &&
                    this.userCompanies.length
                ) {
                    this.isSnackbarShown = true
                }
            },

            hideSnackbar() {
                this.isSnackbarShown = false
                const hideUntil = new Date().getTime() + 24 * 60 * 60 * 1000
                localStorage.setItem('hideSnackbarUntil', hideUntil)
            },
            handleAuthorizedPermission() {
                loadFacebookPixel()
            },

            fixUrl() {
                const url = window.location.href

                if (url.includes('fbclid')) {
                    const urlObj = new URL(url)
                    const finalUrl = url.replace(urlObj.search, '')
                    window.location.replace(finalUrl)
                }
            },
        },

        watch: {
            $route() {
                this.closeSidenav()
            },
        },

        beforeMount() {
            window.addEventListener('authorized-tracking-permission', this.handleAuthorizedPermission)
        },

        mounted() {
            if (this.$route.query.device === 'iOS') {
                localStorage.setItem('deviceIphone', true)
                this.userIsOnIphone = true
            }
            this.showCompleteProfileModal()
            if (localStorage.getItem('cookieConsent') === 'true') {
                loadFacebookPixel()
            }
            if (this.$auth.getUser().id && this.$auth.isLoggedIn()) {
                this.companyOutstandingAmount()
            }

            if (!AuthService.isLoggedIn() && this.showGSignupButton() && !this.userIsOnIphone) {
                googleOneTap()
                    .then((response) => {
                        AuthAPI.loginGoogleOneTap({
                            accessToken: response.credential,
                            language: this.$i18n.locale,
                        })
                            .then((res) => {
                                this.createSession(res.payload)
                            })
                            .catch(() => {})
                    })
                    .catch(() => {
                        this.$toast.error(this.$t('common.errors.common_error'))
                    })
            }

            this.fixUrl()
        },

        destroyed() {
            window.removeEventListener('authorized-tracking-permission', this.handleAuthorizedPermission)
        },
    }
</script>
